import {
  Button,
  Container,
  Grid,
  Input,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import {
  SMSMessage,
  SMSMessageSendRequest,
  SMSMessageSuggestion,
  SMSSession,
} from '../common/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getSMSSession, sendSMS } from '../common/api/sms';
import { ChatTurn } from './ChatTurn';

export type SMSChatBoxProps = {
  session: SMSSession;
};

export function SMSChatBox(props: SMSChatBoxProps) {
  const [session, setSession] = useState(props.session);

  const messages = useMemo(() => {
    const now = new Date();
    const sortedMessages = session.messages
      .map((m) => {
        return {
          ...m,
          suggestions: m.suggestions.sort((a, b) => {
            return a.rank - b.rank;
          }) as SMSMessageSuggestion[],
        } as SMSMessage;
      })
      // sort from oldest to newest
      .sort((a, b) => {
        return (
          (a.sent_at ? new Date(a.sent_at + 'Z') : now).getTime() -
          (b.sent_at ? new Date(b.sent_at + 'Z') : now).getTime()
        );
      });

    return sortedMessages as SMSMessage[];
  }, [session.messages]);

  const [message, setMessage] = useState('');

  const getReq = useMutation({
    mutationFn: () => {
      return getSMSSession(session.uuid);
    },
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (data && JSON.stringify(session) !== JSON.stringify(data)) {
        setSession(data);
      }
    },
  });

  const sendReq = useMutation({
    mutationFn: (data: SMSMessageSendRequest) => {
      return sendSMS(session.uuid, data);
    },
    onSuccess: (data) => {
      console.log('sendReq onSuccess', data);
      if (data) setSession(data);
    },
  });

  const loading = sendReq.isPending;
  const error = sendReq.error;

  const handleSend = () => {
    if (loading) {
      return;
    }
    if (message.trim() === '') {
      return;
    }
    sendReq.mutate({
      message: message.trim(),
    });
    setMessage('');
  };

  const handleSuggestionClick = (uuid: string, type?: 'session' | 'message') => {
    if (loading) {
      return;
    }
    sendReq.mutate({
      suggestion_uuid: uuid,
      suggestion_type: type,
    });
    setMessage('');
  };

  const chatContentRef = createRef<HTMLDivElement>();
  const lastMessageId = useRef(
    messages && messages.length > 0 ? messages[messages.length - 1].uuid : undefined,
  );

  useEffect(() => {
    if (lastMessageId.current) {
      const lastMessage =
        messages && messages.length > 0 ? messages[messages.length - 1] : undefined;
      if (lastMessage && lastMessage.uuid !== lastMessageId.current) {
        lastMessageId.current = lastMessage.uuid;
        // only scroll if the ID of the last message has changed
        if (chatContentRef.current) {
          chatContentRef.current.scrollTop = chatContentRef.current?.scrollHeight;
        }
      }
    } else {
      if (messages && messages.length > 0) {
        lastMessageId.current = messages[messages.length - 1].uuid;
      }
    }
  }, [messages]);

  // recurring poll
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      // TODO call poll function better to look for changes
      if (session.status === 'active' && session.messages.length > 0) {
        await getReq.mutateAsync();
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [getReq, session]);

  return (
    <SpaceBetween direction="vertical" size="xs">
      {/* Message list (scrollable) */}
      <div ref={chatContentRef} className="max-h-[60dvh] overflow-y-auto p-4 bg-white">
        <div>
          <div className="flex flex-col px-4 space-y-4 mx-auto">
            {messages.length > 0 ? (
              messages.map((item, index) => (
                <ChatTurn
                  key={`turn-${item.uuid}`}
                  index={index}
                  loading={loading}
                  message={item}
                  hideSuggestions={index < messages.length - 1}
                  sendSuggestion={handleSuggestionClick}
                  contact={session.place_contact ?? undefined}
                />
              ))
            ) : (
              <div key="turn-div-suggest">
                <ChatTurn
                  key="turn-suggest"
                  sendSuggestion={handleSuggestionClick}
                  index={0}
                  loading={loading}
                  suggestionType="session"
                  contact={session.place_contact ?? undefined}
                  message={{
                    uuid: 'initial-suggestions',
                    author: 'assistant',
                    message:
                      'Here are some suggestions for you to start a conversation with the client.',
                    message_hash: '',
                    sent_at: new Date().toISOString().split('.')[0],
                    status: 'pending',
                    created_at: new Date().toISOString().split('.')[0],
                    updated_at: new Date().toISOString().split('.')[0],
                    suggestions: session.suggestions.map((s) => ({
                      uuid: s.uuid,
                      message: s.message,
                      rank: s.rank,
                      model: s.model,
                      selected: s.selected,
                      created_at: s.created_at,
                      updated_at: s.updated_at,
                    })),
                  }}
                />
              </div>
            )}
          </div>
          {/* TODO better error viewing */}
          {/* TODO would prefer more of a popup error as this takes up space */}
          {/* TODO popup tost errors instead */}
          {/* {error ? (
          <Alert header="Error" statusIconAriaLabel="Error" type="error">
            {error}
          </Alert>
        ) : undefined} */}
          {/* TODO I need to add a loading spinner here when loading */}
          {loading && (
            <div className="flex justify-center items-center">
              <Spinner size="normal" />
            </div>
          )}
        </div>
      </div>
      {/* Chat Bar */}
      <Grid gridDefinition={[{ colspan: { default: 12, m: 8 }, offset: { default: 0, m: 2 } }]}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <div
            style={{
              flex: 1,
              marginRight: '8px',
            }}
          >
            <Input
              value={message}
              onChange={(e) => setMessage(e.detail.value)}
              onKeyDown={(e) => {
                if (e.detail.key === 'Enter') {
                  handleSend();
                }
              }}
              disabled={loading}
              placeholder="Type a text message..."
            />
          </div>
          <Button
            disabled={loading}
            iconName="arrow-right"
            variant="primary"
            onClick={handleSend}
          />
        </div>
      </Grid>
    </SpaceBetween>
  );
}
